/**
 * MODULE: _shared/frontend-shell/src/hooks/billing/useInvoices.ts
 * PURPOSE: Fetch merged Stripe + credit top-up invoice history
 */
import { useQuery } from '@tanstack/react-query';
import { getAuthHeaders, getApiBaseUrl } from '@shared/lib/frontend/api/config';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth`;

export type BillingInvoice = {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: 'paid' | 'due' | 'void' | string;
  description: string;
  pdf_url?: string | null;
  source: 'stripe_subscription' | 'credit_topup' | string;
};

async function fetchInvoices(): Promise<BillingInvoice[]> {
  const response = await fetch(`${API_BASE}/subscription/invoices`, {
    headers: getAuthHeaders(),
  });
  if (response.status === 403) {
    return [];
  }
  if (!response.ok) {
    throw new Error('Failed to load invoice history');
  }
  const data = (await response.json()) as { invoices?: BillingInvoice[] };
  return data.invoices ?? [];
}

export function useInvoices() {
  return useQuery<BillingInvoice[], Error>({
    queryKey: ['billing', 'invoices'],
    queryFn: fetchInvoices,
    staleTime: 60_000,
  });
}
